import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaImage,
  FaYoutube,
  FaRupeeSign,
  FaTag,
  FaLayerGroup,
  FaExpand,
} from "react-icons/fa";

import "./Modules.css";
import { BASE_URL } from "../../Config/config";

export default function Modules() {
  /* =====================================================
     BASE URL
  ===================================================== */

  const MODULE_API = `${BASE_URL}/modules.php`;

  /* =====================================================
     MODULE STATE
  ===================================================== */

  const [modules, setModules] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingModule, setEditingModule] = useState(null);

  /* =====================================================
     CATEGORY MANAGER
  ===================================================== */

  const [categories, setCategories] = useState([]);

  const [newCategory, setNewCategory] = useState("");
  const [categoryDisplayOrder, setCategoryDisplayOrder] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);

  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryEditName, setCategoryEditName] = useState("");
  const [categorySaving, setCategorySaving] = useState(false);

  /* =====================================================
     DESCRIPTION POPUP
  ===================================================== */

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState(null);

  /* =====================================================
     IMAGE POPUP
  ===================================================== */

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  /* =====================================================
   PAGINATION
===================================================== */

  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 20;
  /* =====================================================
   PAGINATION
===================================================== */

  const totalPages = Math.ceil(modules.length / itemsPerPage);

  const paginatedModules = modules.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  /* =====================================================
   RESET PAGE AFTER MODULE DATA CHANGES
===================================================== */

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }

    if (modules.length === 0) {
      setCurrentPage(1);
    }
  }, [modules.length, currentPage, totalPages]);
  useEffect(() => {
    const pages = Math.ceil(modules.length / itemsPerPage);

    if (modules.length === 0) {
      setCurrentPage(1);
    } else if (currentPage > pages) {
      setCurrentPage(pages);
    }
  }, [modules.length, currentPage]);
  /* =====================================================
     FORM STATE
  ===================================================== */

  const [form, setForm] = useState({
    title: "",
    code: "",
    price: "",
    renew: "",
    description: "",
    category: "",
    image: null,
    youtube: "",
    display_order: "",
  });

  /* =====================================================
     CATEGORY EDIT
  ===================================================== */

  const startEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryEditName(category.name);
  };
  const cancelEditCategory = () => {
    setEditingCategory(null);
    setCategoryEditName("");
  };

  /* =====================================================
     UPDATE CATEGORY
  ===================================================== */

  const updateCategory = async () => {
    const name = categoryEditName.trim();

    if (!name) {
      alert("Category name is required.");
      return;
    }

    if (!editingCategory) {
      return;
    }

    try {
      setCategorySaving(true);

      const formData = new FormData();

      formData.append("id", editingCategory.id);
      formData.append("name", name);
      formData.append("display_order", editingCategory.display_order ?? 0);
      const response = await fetch(`${MODULE_API}?action=update_category`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!result.status) {
        alert(result.message || "Failed to update category.");
        return;
      }

      alert("Category updated successfully.");

      await fetchCategories();

      if (form.category === editingCategory.name) {
        setForm((prev) => ({
          ...prev,
          category: name,
        }));
      }

      cancelEditCategory();
    } catch (error) {
      console.error("Update category error:", error);

      alert("Unable to update category.");
    } finally {
      setCategorySaving(false);
    }
  };
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });

    AOS.refresh();
  }, []);
  /* =====================================================
     DELETE CATEGORY
  ===================================================== */

  const deleteCategory = async (category) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${category.name}"?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      const formData = new FormData();

      formData.append("id", category.id);

      const response = await fetch(`${MODULE_API}?action=delete_category`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!result.status) {
        alert(result.message || "Failed to delete category.");
        return;
      }

      alert("Category deleted successfully.");

      if (form.category === category.name) {
        setForm((prev) => ({
          ...prev,
          category: "",
        }));
      }

      await fetchCategories();
    } catch (error) {
      console.error("Delete category error:", error);

      alert("Unable to delete category.");
    }
  };

  /* =====================================================
     FETCH MODULES
  ===================================================== */

  const fetchModules = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${MODULE_API}?action=get`);

      const result = await response.json();

      if (result.status) {
        setModules(Array.isArray(result.data) ? result.data : []);
      } else {
        alert(result.message || "Failed to fetch modules.");
      }
    } catch (error) {
      console.error("Fetch modules error:", error);

      alert("Unable to connect with module backend.");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     FETCH CATEGORIES
  ===================================================== */

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${MODULE_API}?action=get_categories`);

      const result = await response.json();

      if (result.status) {
        setCategories(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error(result.message || "Failed to fetch categories.");

        setCategories([]);
      }
    } catch (error) {
      console.error("Fetch categories error:", error);

      setCategories([]);
    }
  };

  /* =====================================================
     INITIAL FETCH
  ===================================================== */

  useEffect(() => {
    fetchModules();
    fetchCategories();
  }, []);

  /* =====================================================
     OPEN ADD FORM
  ===================================================== */

  const openAddForm = () => {
    setEditingModule(null);

    setForm({
      title: "",
      code: "",
      price: "",
      description: "",
      category: "",
      image: null,
      youtube: "",
      display_order: "",
    });

    setNewCategory("");
    setShowAddCategory(false);

    setShowForm(true);
  };

  /* =====================================================
     OPEN EDIT FORM
  ===================================================== */

  const openEditForm = (module) => {
    setEditingModule(module);

    setForm({
      title: module.title || "",
      code: module.code || "",
      price: module.price || "",
      renew: module.renew || "",
      description: module.description || "",
      category: module.category || "",
      image: null,
      youtube: module.youtube || "",
      display_order: module.display_order || "",
    });

    setNewCategory("");
    setShowAddCategory(false);

    setShowForm(true);
  };

  /* =====================================================
     CLOSE FORM
  ===================================================== */

  const closeForm = () => {
    setShowForm(false);

    setEditingModule(null);

    setForm({
      title: "",
      code: "",
      price: "",
      renew: "",
      description: "",
      category: "",
      image: null,
      youtube: "",
      display_order: "",
    });

    setNewCategory("");
    setShowAddCategory(false);
  };

  /* =====================================================
     HANDLE FORM CHANGE
  ===================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =====================================================
     HANDLE IMAGE
  ===================================================== */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;

    setForm((prev) => ({
      ...prev,
      image: file,
    }));
  };

  /* =====================================================
     ADD CATEGORY
  ===================================================== */

  const addCategory = async () => {
    const category = newCategory.trim();

    if (!category) {
      alert("Please enter category name.");
      return;
    }

    try {
      const formData = new FormData();

      formData.append("name", category);
      formData.append("display_order", categoryDisplayOrder || 0);
      const response = await fetch(`${MODULE_API}?action=add_category`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!result.status) {
        alert(result.message || "Failed to add category.");
        return;
      }

      await fetchCategories();

      if (result.data?.name) {
        setForm((prev) => ({
          ...prev,
          category: result.data.name,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          category: category,
        }));
      }
      setNewCategory("");
      setCategoryDisplayOrder("");
      setShowAddCategory(false);
      alert(
        result.data?.exists
          ? "Category already exists."
          : "Category added successfully.",
      );
    } catch (error) {
      console.error("Add category error:", error);

      alert("Unable to add category.");
    }
  };

  /* =====================================================
     SAVE MODULE
  ===================================================== */

  const saveModule = async () => {
    if (!form.title.trim()) {
      alert("Module title is required.");
      return;
    }

    if (!form.code.trim()) {
      alert("Module code is required.");
      return;
    }

    if (!form.price.trim()) {
      alert("Module price is required.");
      return;
    }

    if (!form.description.trim()) {
      alert("Module description is required.");
      return;
    }

    if (!form.category) {
      alert("Please select category.");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("title", form.title.trim());
      formData.append("code", form.code.trim());
      formData.append("price", form.price.trim());
      formData.append("renew", form.renew.trim());
      formData.append("description", form.description.trim());
      formData.append("category", form.category);
      formData.append("youtube", form.youtube.trim());
      formData.append("display_order", form.display_order);
      if (form.image) {
        formData.append("image", form.image);
      }

      let action = "add";

      if (editingModule) {
        action = "update";

        formData.append("id", editingModule.id);
      }

      const response = await fetch(`${MODULE_API}?action=${action}`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!result.status) {
        alert(result.message || "Failed to save module.");
        return;
      }

      alert(
        editingModule
          ? "Module updated successfully."
          : "Module added successfully.",
      );

      await fetchModules();

      closeForm();
    } catch (error) {
      console.error("Save module error:", error);

      alert("Unable to save module.");
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
     DELETE MODULE
  ===================================================== */

  const deleteModule = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this module?",
    );

    if (!confirmed) {
      return;
    }

    try {
      const formData = new FormData();

      formData.append("id", id);

      const response = await fetch(`${MODULE_API}?action=delete`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!result.status) {
        alert(result.message || "Failed to delete module.");
        return;
      }

      alert("Module deleted successfully.");

      await fetchModules();
    } catch (error) {
      console.error("Delete module error:", error);

      alert("Unable to delete module.");
    }
  };

  /* =====================================================
     IMAGE URL
  ===================================================== */

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${BASE_URL}/${image.replace(/^\/+/, "")}`;
  };

  /* =====================================================
     DESCRIPTION HELPER
  ===================================================== */

  const getShortDescription = (description, limit = 100) => {
    if (!description) {
      return "No description";
    }

    if (description.length <= limit) {
      return description;
    }

    return `${description.substring(0, limit)}...`;
  };

  /* =====================================================
     OPEN DESCRIPTION POPUP
  ===================================================== */

  const openDescription = (module) => {
    setSelectedDescription(module);
    setShowDescriptionModal(true);
  };

  /* =====================================================
     CLOSE DESCRIPTION POPUP
  ===================================================== */

  const closeDescription = () => {
    setShowDescriptionModal(false);
    setSelectedDescription(null);
  };

  /* =====================================================
     OPEN IMAGE POPUP
  ===================================================== */

  const openImagePopup = (module) => {
    if (!module.image) {
      return;
    }

    setSelectedImage({
      url: getImageUrl(module.image),
      title: module.title || "Module Image",
    });

    setShowImageModal(true);
  };

  /* =====================================================
     CLOSE IMAGE POPUP
  ===================================================== */

  const closeImagePopup = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  /* =====================================================
     IMAGE PREVIEW
  ===================================================== */

  const imagePreview = form.image ? URL.createObjectURL(form.image) : null;

  /* =====================================================
     RETURN
  ===================================================== */

  return (
    <div className="admin-modules-page" data-aos="fade-up">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        className="admin-page-header"
        data-aos="fade-down"
        data-aos-duration="700"
      >
        <div>
          <span className="admin-page-label">TALLY MODULE MANAGEMENT</span>

          <h1>Modules</h1>

          <p>
            Manage Tally modules, pricing, categories, images, descriptions and
            other module information displayed on the website.
          </p>
        </div>

        <div className="admin-page-header-actions">
          <button
            type="button"
            className="admin-category-manager-btn"
            onClick={() => {
              setEditingCategory(null);
              setCategoryEditName("");
              setShowCategoryManager(true);
            }}
          >
            <FaTag />
            Manage Categories
          </button>

          <button
            type="button"
            className="admin-primary-btn"
            onClick={openAddForm}
          >
            <FaPlus />
            Add Module
          </button>
        </div>
      </div>

      {/* =====================================================
          SUMMARY
      ===================================================== */}

      <div className="admin-module-summary">
        <div className="module-summary-card">
          <div className="summary-icon">
            <FaLayerGroup />
          </div>

          <div>
            <span>Total Modules</span>
            <strong>{modules.length}</strong>
          </div>
        </div>

        <div className="module-summary-card">
          <div className="summary-icon">
            <FaTag />
          </div>

          <div>
            <span>Categories</span>
            <strong>{categories.length}</strong>
          </div>
        </div>

        {/* <div className="module-summary-card">
          <div className="summary-icon">
            <FaImage />
          </div>

          <div>
            <span>Modules With Images</span>

            <strong>{modules.filter((item) => item.image).length}</strong>
          </div>
        </div> */}
      </div>

      {/* =====================================================
          MODULE LIST
      ===================================================== */}

      <div
        className="admin-modules-section"
        data-aos="fade-up"
        data-aos-delay="200"
      >
        <div className="admin-section-heading">
          <div>
            <span>MODULE LIST</span>
            <h2>Manage Modules</h2>
          </div>

          <strong>
            {modules.length} {modules.length === 1 ? "Module" : "Modules"}
          </strong>
        </div>

        {/* LOADING */}

        {loading ? (
          <div className="admin-empty-modules">
            <FaLayerGroup />

            <h3>Loading Modules...</h3>

            <p>Please wait while modules are being loaded.</p>
          </div>
        ) : modules.length > 0 ? (
          /* =====================================================
             TABLE
          ===================================================== */

          <div className="admin-modules-table-wrapper">
            <table className="admin-modules-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Module</th>
                  <th>Code</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Renew</th>
                  <th>Description</th>
                  <th>YouTube</th>
                  <th>Display Order</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginatedModules.map((module, index) => (
                  <tr key={module.id}>
                    {/* NUMBER */}

                    {/* <td>
                      <span className="module-table-number">
                        {modules.length - index + 1}
                      </span>
                    </td> */}
                    <td>
                      <span className="table-number">
                        {String(
                          modules.length -
                            ((currentPage - 1) * itemsPerPage + index),
                        ).padStart(2, "0")}
                      </span>
                    </td>
                    {/* IMAGE */}

                    <td>
                      <div
                        className="module-table-image clickable"
                        onClick={() => openImagePopup(module)}
                        title={module.image ? "View image" : "No image"}
                      >
                        {module.image ? (
                          <>
                            <img
                              src={getImageUrl(module.image)}
                              alt={module.title}
                            />

                            <div className="module-image-overlay">
                              <FaExpand />
                            </div>
                          </>
                        ) : (
                          <div className="module-table-no-image">
                            <FaImage />
                          </div>
                        )}
                      </div>
                    </td>

                    {/* TITLE */}

                    <td>
                      <div className="module-table-title">
                        <strong>{module.title}</strong>

                        {module.code && <span>Code: {module.code}</span>}
                      </div>
                    </td>

                    {/* CODE */}

                    <td>
                      <span className="module-code-badge">
                        {module.code || "-"}
                      </span>
                    </td>

                    {/* CATEGORY */}

                    <td>
                      <span className="module-category-badge">
                        <FaTag />

                        {module.category || "-"}
                      </span>
                    </td>

                    {/* PRICE */}

                    <td>
                      <div className="module-table-price">
                        <FaRupeeSign />

                        <strong>{module.price || "0"}</strong>
                      </div>
                    </td>
                    <td>
                      <div className="module-table-price">
                        <FaRupeeSign />

                        <strong>{module.renew || "0.00"}</strong>
                      </div>
                    </td>

                    {/* DESCRIPTION */}

                    <td>
                      {module.description ? (
                        module.description.length > 100 ? (
                          <button
                            type="button"
                            className="module-description-btn"
                            onClick={() => openDescription(module)}
                            title="Click to view full description"
                          >
                            {getShortDescription(module.description, 100)}

                            <span>View More</span>
                          </button>
                        ) : (
                          <div className="module-description-text">
                            {module.description}
                          </div>
                        )
                      ) : (
                        <span className="module-no-description">
                          No description
                        </span>
                      )}
                    </td>

                    {/* YOUTUBE */}

                    <td>
                      {module.youtube ? (
                        <a
                          href={module.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="module-youtube-btn"
                        >
                          <FaYoutube />
                          Watch
                        </a>
                      ) : (
                        <span className="module-no-youtube">-</span>
                      )}
                    </td>
                    <td>{module.display_order}</td>
                    {/* ACTIONS */}

                    <td>
                      <div className="module-table-actions">
                        <button
                          type="button"
                          className="module-edit-btn"
                          onClick={() => openEditForm(module)}
                          title="Edit module"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          className="module-delete-btn"
                          onClick={() => deleteModule(module.id)}
                          title="Delete module"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* =====================================================
             EMPTY
          ===================================================== */

          <div className="admin-empty-modules">
            <FaLayerGroup />

            <h3>No Modules Added</h3>

            <p>
              Start adding Tally modules that should appear on your website.
            </p>

            <button type="button" onClick={openAddForm}>
              <FaPlus />
              Add First Module
            </button>
          </div>
        )}
      </div>
      {/* =====================================================
    PAGINATION
===================================================== */}

      {totalPages > 1 && (
        <div className="admin-pagination">
          {/* PREVIOUS */}

          <button
            type="button"
            className="admin-pagination-btn"
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {/* PAGE NUMBERS */}

          <div className="admin-pagination-pages">
            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;

              return (
                <button
                  type="button"
                  key={page}
                  className={`admin-pagination-page ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              );
            })}
          </div>

          {/* NEXT */}

          <button
            type="button"
            className="admin-pagination-btn"
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
      {/* =====================================================
          ADD / EDIT MODAL
      ===================================================== */}

      {showForm && (
        <div className="admin-modal-overlay">
          <div className="admin-modal admin-module-modal">
            {/* HEADER */}

            <div className="admin-modal-header">
              <div>
                <span>TALLY MODULE</span>

                <h2>{editingModule ? "Edit Module" : "Add Module"}</h2>
              </div>

              <button type="button" onClick={closeForm}>
                <FaTimes />
              </button>
            </div>

            {/* FORM */}

            <div className="admin-form">
              {/* TITLE */}

              <label>
                Module Title
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="GST Sales Invoice Automation"
                />
              </label>

              {/* CODE */}

              <label>
                Module Code
                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="295"
                />
              </label>

              {/* PRICE */}

              <label>
                Price
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="3200.00"
                  min="0"
                  step="0.01"
                />
              </label>

              <label>
                Renew
                <input
                  type="number"
                  name="renew"
                  value={form.renew}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </label>

              {/* DESCRIPTION */}

              <label>
                Description
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Enter module description..."
                  rows="5"
                />
              </label>

              {/* CATEGORY */}

              <label>
                Category
                <div className="admin-category-select-row">
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                  >
                    <option value="">Select Category</option>

                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    className="admin-add-category-btn"
                    onClick={() => setShowAddCategory((prev) => !prev)}
                  >
                    <FaPlus />
                    Add
                  </button>
                </div>
              </label>

              {/* ADD CATEGORY */}

              {showAddCategory && (
                <div className="admin-add-category-box">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter new category name"
                  />

                  <label>
                    Display Order
                    <input
                      type="number"
                      name="display_order"
                      value={form.display_order}
                      onChange={handleChange}
                      placeholder="1"
                      min="0"
                      step="1"
                    />
                  </label>
                  <button
                    type="button"
                    className="admin-save-category-btn"
                    onClick={addCategory}
                  >
                    <FaSave />
                    Add Category
                  </button>
                </div>
              )}

              {/* IMAGE */}

              <label>
                Module Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </label>

              {/* EXISTING IMAGE */}

              {editingModule && !form.image && editingModule.image && (
                <div className="admin-image-preview">
                  <img
                    src={getImageUrl(editingModule.image)}
                    alt="Current module"
                  />

                  <span>Current image</span>
                </div>
              )}

              {/* NEW IMAGE */}

              {imagePreview && (
                <div className="admin-image-preview">
                  <img src={imagePreview} alt="Preview" />

                  <span>New image selected</span>
                </div>
              )}

              {/* YOUTUBE */}

              <label>
                YouTube URL
                <input
                  type="url"
                  name="youtube"
                  value={form.youtube}
                  onChange={handleChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </label>
              <label>
                Display Order
                <input
                  type="url"
                  name="display_order"
                  value={form.display_order}
                  onChange={handleChange}
                />
              </label>
              {/* SAVE */}

              <button
                type="button"
                className="admin-save-btn"
                onClick={saveModule}
                disabled={saving}
              >
                <FaSave />

                {saving
                  ? "Saving..."
                  : editingModule
                    ? "Update Module"
                    : "Save Module"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          DESCRIPTION POPUP
      ===================================================== */}

      {showDescriptionModal && selectedDescription && (
        <div className="admin-modal-overlay" onClick={closeDescription}>
          <div
            className="admin-modal module-description-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}

            <div className="admin-modal-header">
              <div>
                <span>MODULE DESCRIPTION</span>

                <h2>{selectedDescription.title}</h2>
              </div>

              <button type="button" onClick={closeDescription}>
                <FaTimes />
              </button>
            </div>

            {/* CONTENT */}

            <div className="module-description-popup-content">
              <div className="module-description-popup-info">
                <div>
                  <span>Module Code</span>

                  <strong>{selectedDescription.code || "-"}</strong>
                </div>

                <div>
                  <span>Category</span>

                  <strong>{selectedDescription.category || "-"}</strong>
                </div>

                <div>
                  <span>Price</span>

                  <strong>₹{selectedDescription.price || "0"}</strong>
                </div>
              </div>

              <div className="module-full-description">
                <h3>Description</h3>

                <p>{selectedDescription.description}</p>
              </div>

              {selectedDescription.youtube && (
                <a
                  href={selectedDescription.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="module-popup-youtube"
                >
                  <FaYoutube />
                  Watch YouTube Video
                </a>
              )}
            </div>

            {/* FOOTER */}

            <div className="module-description-popup-footer">
              <button
                type="button"
                onClick={closeDescription}
                className="category-manager-close-btn"
              >
                <FaTimes />
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          IMAGE POPUP
      ===================================================== */}

      {showImageModal && selectedImage && (
        <div className="admin-image-modal-overlay" onClick={closeImagePopup}>
          <div
            className="admin-image-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* CLOSE */}

            <button
              type="button"
              className="admin-image-modal-close"
              onClick={closeImagePopup}
              aria-label="Close image"
            >
              <FaTimes />
            </button>

            {/* IMAGE */}

            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="admin-image-modal-img"
            />

            {/* TITLE */}

            <div className="admin-image-modal-caption">
              <strong>{selectedImage.title}</strong>

              <span>Click outside the image or press the close button</span>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          CATEGORY MANAGEMENT MODAL
      ===================================================== */}

      {showCategoryManager && (
        <div className="admin-modal-overlay">
          <div className="admin-modal admin-category-manager-modal">
            {/* HEADER */}

            <div className="admin-modal-header">
              <div>
                <span>MODULE CATEGORIES</span>

                <h2>Manage Categories</h2>

                <p>Add, edit or delete module categories.</p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowCategoryManager(false);

                  cancelEditCategory();
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* ADD CATEGORY */}

            <div className="category-manager-add">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Enter new category name"
              />
              <input
                type="number"
                min="0"
                step="1"
                value={categoryDisplayOrder}
                onChange={(e) => setCategoryDisplayOrder(e.target.value)}
                placeholder="Display Order"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCategory();
                  }

                  if (e.key === "Escape") {
                    setCategoryDisplayOrder("");
                  }
                }}
              />
              <button
                type="button"
                className="category-manager-add-btn"
                onClick={addCategory}
              >
                <FaPlus />
                Add Category
              </button>
            </div>

            {/* CATEGORY COUNT */}

            <div className="category-manager-info">
              <div>
                <FaTag />

                <span>
                  {categories.length}{" "}
                  {categories.length === 1 ? "Category" : "Categories"}
                </span>
              </div>
            </div>

            {/* CATEGORY LIST */}

            {/* CATEGORY LIST */}

            <div className="category-manager-list">
              {categories.length === 0 ? (
                <div className="category-manager-empty">
                  <FaTag />

                  <h3>No Categories</h3>

                  <p>Add your first module category above.</p>
                </div>
              ) : (
                <table className="category-manager-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Display Order</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {categories.map((category) => (
                      <React.Fragment key={category.id}>
                        {editingCategory?.id === category.id ? (
                          <tr className="category-manager-edit-row">
                            <td>
                              <input
                                type="text"
                                value={categoryEditName}
                                onChange={(e) =>
                                  setCategoryEditName(e.target.value)
                                }
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    updateCategory();
                                  }

                                  if (e.key === "Escape") {
                                    cancelEditCategory();
                                  }
                                }}
                              />
                            </td>

                            <td>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={editingCategory?.display_order ?? 0}
                                onChange={(e) =>
                                  setEditingCategory((prev) => ({
                                    ...prev,
                                    display_order: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    updateCategory();
                                  }

                                  if (e.key === "Escape") {
                                    cancelEditCategory();
                                  }
                                }}
                              />
                            </td>

                            <td>
                              <div className="category-manager-actions">
                                <button
                                  type="button"
                                  className="category-save-btn"
                                  onClick={updateCategory}
                                  disabled={categorySaving}
                                >
                                  <FaSave />
                                  {categorySaving ? "Saving..." : "Save"}
                                </button>

                                <button
                                  type="button"
                                  className="category-cancel-btn"
                                  onClick={cancelEditCategory}
                                  disabled={categorySaving}
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td>
                              <div className="category-manager-name">
                                <div className="category-manager-icon">
                                  <FaTag />
                                </div>

                                <strong>{category.name}</strong>
                              </div>
                            </td>

                            <td>
                              <strong className="category-order-value">
                                {category.display_order ?? 0}
                              </strong>
                            </td>

                            <td>
                              <div className="category-manager-actions">
                                <button
                                  type="button"
                                  className="category-edit-btn"
                                  onClick={() => startEditCategory(category)}
                                  title="Edit category"
                                >
                                  <FaEdit />
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="category-delete-btn"
                                  onClick={() => deleteCategory(category)}
                                  title="Delete category"
                                >
                                  <FaTrash />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* FOOTER */}

            <div className="category-manager-footer">
              <button
                type="button"
                className="category-manager-close-btn"
                onClick={() => {
                  setShowCategoryManager(false);

                  cancelEditCategory();
                }}
              >
                <FaTimes />
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
